import {
  IClientsInitialState,
  IClientState,
  IPointOfSalesExchangeRate,
  IPointOfSaleState,
  TclientDataToUpgradeItem,
} from 'interfaces/clients.interface';
import * as ClientsTypes from './clients.types';
import { IGetBrandSegmentationsAPIResponseItem } from 'api/endpoints/clients/clients.response.interface';

export type TclientsDispatchReducer =
  | {
      type: typeof ClientsTypes.SET_CLIENTS;
      payload: Array<IClientState>;
    }
  | {
      type: typeof ClientsTypes.SET_IS_LOADING_GET_CLIENTS;
      payload: boolean;
    }
  | {
      type: typeof ClientsTypes.SELECT_CLIENT;
      payload: IClientState;
    }
  | {
      type: typeof ClientsTypes.IS_LOADING_SELECT_CLIENT;
      payload: boolean;
    }
  | {
      type: typeof ClientsTypes.SEARCH_CLIENTS;
      payload: {
        items: Array<IClientState>;
        query: string;
      };
    }
  | {
      type: typeof ClientsTypes.SEARCH_POINTS_OF_SALE;
      payload: {
        items: Array<IPointOfSaleState>;
        query: string;
      };
    }
  | {
      type: typeof ClientsTypes.SET_POINTS_OF_SALE;
      payload: Array<IPointOfSaleState>;
    }
  | {
      type: typeof ClientsTypes.CREATE_CLIENT;
      payload: IClientState;
    }
  | {
      type: typeof ClientsTypes.UPDATE_CLIENT;
      payload: {
        client: Partial<IClientState>;
        clientId: string;
      };
    }
  | {
      type: typeof ClientsTypes.SET_IS_LOADING_UPDATE_CLIENT;
      payload: boolean;
    }
  | {
      type: typeof ClientsTypes.DELETE_CLIENT;
      payload: string;
    }
  | {
      type: typeof ClientsTypes.SELECT_POINT_OF_SALE;
      payload: IPointOfSaleState | null;
    }
  | {
      type: typeof ClientsTypes.CREATE_POINT_OF_SALE;
      payload: IPointOfSaleState;
    }
  | {
      type: typeof ClientsTypes.UPDATE_POINT_OF_SALE;
      payload: IClientState;
    }
  | {
      type: typeof ClientsTypes.REMOVE_POINT_OF_SALE;
      payload: string;
    }
  | {
      type: typeof ClientsTypes.REFRESH_CLIENTS;
      payload: boolean;
    }
  | {
      type: typeof ClientsTypes.SET_SEGMENTATIONS;
      payload: IGetBrandSegmentationsAPIResponseItem;
    }
  | {
      type: typeof ClientsTypes.UPDATE_EXCHANGE_RATE;
      payload: Array<IPointOfSalesExchangeRate>;
    }
  | {
      type: typeof ClientsTypes.ADD_NEW_EXCHANGE_RATE;
      payload: {
        exchangeRate: IPointOfSalesExchangeRate;
        pointOfSaleId: string;
      };
    };

export default function clientsReducer(
  state: IClientsInitialState,
  action: TclientsDispatchReducer
) {
  const { type, payload } = action;

  switch (type) {
    case ClientsTypes.SET_CLIENTS: {
      const setClients: IClientsInitialState = {
        ...state,
        clients: payload,
      };
      return setClients;
    }

    case ClientsTypes.SELECT_CLIENT: {
      const selectClient: IClientsInitialState = {
        ...state,
        clientSelected: payload,
      };
      return selectClient;
    }

    case ClientsTypes.IS_LOADING_SELECT_CLIENT: {
      const isLoadingSelectClient: IClientsInitialState = {
        ...state,
        isLoadingSelectClient: payload,
      };
      return isLoadingSelectClient;
    }

    case ClientsTypes.SEARCH_CLIENTS: {
      const searchClients: IClientsInitialState = {
        ...state,
        searchedClients: {
          items: payload.items,
          query: payload.query,
        },
      };
      return searchClients;
    }

    case ClientsTypes.SEARCH_POINTS_OF_SALE: {
      const searchPointsOfSale: IClientsInitialState = {
        ...state,
        searchedPointsOfSale: {
          items: payload.items,
          query: payload.query,
        },
      };
      return searchPointsOfSale;
    }

    case ClientsTypes.SET_POINTS_OF_SALE: {
      const setPointsOfSale: IClientsInitialState = {
        ...state,
        pointsOfSale: payload,
      };
      return setPointsOfSale;
    }

    case ClientsTypes.SELECT_POINT_OF_SALE: {
      const selectPointOfSale: IClientsInitialState = {
        ...state,
        pointOfSaleSelected: payload,
      };
      return selectPointOfSale;
    }

    case ClientsTypes.CREATE_CLIENT: {
      const createClient: IClientsInitialState = {
        ...state,
        clients: [...state.clients, payload],
      };
      return createClient;
    }

    case ClientsTypes.UPDATE_CLIENT: {
      const updateClient: IClientsInitialState = {
        ...state,
        clientSelected: {
          ...state.clientSelected,
          ...(payload.client as IClientState),
          clientDataToUpgrade: Array.isArray(state.clientSelected?.clientDataToUpgrade)
            ? (state.clientSelected?.clientDataToUpgrade.filter(
                (item) => !(item.key in payload.client)
              ) as Array<TclientDataToUpgradeItem>)
            : ([] as Array<TclientDataToUpgradeItem>),
        },
        clients: state.clients.map((client) => {
          if (client.key === payload.clientId) {
            return {
              ...client,
              ...(payload.client as IClientState),
              clientDataToUpgrade: Array.isArray(
                state.clientSelected?.clientDataToUpgrade
              )
                ? (state.clientSelected?.clientDataToUpgrade.filter(
                    (item) => !(item.key in payload.client)
                  ) as Array<TclientDataToUpgradeItem>)
                : ([] as Array<TclientDataToUpgradeItem>),
            };
          }
          return client;
        }),
        searchedClients: {
          ...state.searchedClients,
          items: state.searchedClients.items.map((client) => {
            if (client.key === payload.clientId) {
              return {
                ...client,
                ...(payload.client as IClientState),
                clientDataToUpgrade: Array.isArray(
                  state.clientSelected?.clientDataToUpgrade
                )
                  ? (state.clientSelected?.clientDataToUpgrade.filter(
                      (item) => !(item.key in payload.client)
                    ) as Array<TclientDataToUpgradeItem>)
                  : ([] as Array<TclientDataToUpgradeItem>),
              };
            }
            return client;
          }),
        },
      };
      return updateClient;
    }

    case ClientsTypes.SET_IS_LOADING_UPDATE_CLIENT: {
      const setIsLoadingUpdateClient: IClientsInitialState = {
        ...state,
        isLoadingUpdateClient: payload,
      };
      return setIsLoadingUpdateClient;
    }

    case ClientsTypes.DELETE_CLIENT: {
      const removeClient: IClientsInitialState = {
        ...state,
        clients: state.clients.filter((client) => client.key !== payload),
        searchedClients: {
          ...state.searchedClients,
          items: state.searchedClients.items.filter((client) => client.key !== payload),
        },
      };
      return removeClient;
    }

    case ClientsTypes.SET_IS_LOADING_GET_CLIENTS: {
      const setIsLoadingClient: IClientsInitialState = {
        ...state,
        isLoadingGetClients: payload,
      };
      return setIsLoadingClient;
    }

    case ClientsTypes.REFRESH_CLIENTS: {
      const refreshClients: IClientsInitialState = {
        ...state,
        refreshClients: payload,
      };
      return refreshClients;
    }

    case ClientsTypes.CREATE_POINT_OF_SALE: {
      const createPointOfSale: IClientsInitialState = {
        ...state,
        clientSelected: {
          ...state.clientSelected,
          clientSalePoint: [
            ...(state.clientSelected as IClientState).clientSalePoint,
            payload,
          ],
        } as IClientState,
      };
      return createPointOfSale;
    }

    case ClientsTypes.UPDATE_POINT_OF_SALE: {
      const updatePointOfSale: IClientsInitialState = {
        ...state,
        clientSelected: payload,
      };
      return updatePointOfSale;
    }

    case ClientsTypes.REMOVE_POINT_OF_SALE: {
      const removePointOfSale: IClientsInitialState = {
        ...state,
        clientSelected: {
          ...(state.clientSelected as IClientState),
          clientSalePoint: (
            state.clientSelected as IClientState
          )?.clientSalePoint?.filter((point) => point.key !== payload),
          pendingPointsOfSaleAssociations:
            (state.clientSelected as IClientState).pendingPointsOfSaleAssociations - 1,
        },
        pointsOfSale: state.pointsOfSale.filter((point) => point.key !== payload),
        searchedPointsOfSale: {
          ...state.searchedPointsOfSale,
          items: state.searchedPointsOfSale.items.filter(
            (point) => point.key !== payload
          ),
        },
        pointOfSaleSelected: null,
        clients: state.clients.map((client) => {
          if (client.key === (state.clientSelected as IClientState).key) {
            return {
              ...client,
              clientSalePoint: client?.clientSalePoint?.filter(
                (point) => point.key !== payload
              ),
            };
          }
          return client;
        }),
      };
      return removePointOfSale;
    }

    case ClientsTypes.SET_SEGMENTATIONS: {
      const setSegmentations: IClientsInitialState = {
        ...state,
        segmentations: payload,
      };
      return setSegmentations;
    }

    case ClientsTypes.UPDATE_EXCHANGE_RATE: {
      const updateExchangeRate: IClientsInitialState = {
        ...state,
        pointOfSaleSelected: {
          ...(state.pointOfSaleSelected as IPointOfSaleState),
          point_of_sale_exchange_rates: payload,
        },
        clientSelected: {
          ...state.clientSelected,
          clientSalePoint: (state.clientSelected as IClientState)?.clientSalePoint?.map(
            (point) => {
              if (point.key === (state.pointOfSaleSelected as IPointOfSaleState).key) {
                return {
                  ...point,
                  point_of_sale_exchange_rates: payload,
                };
              }
              return point;
            }
          ),
        } as IClientState,
        clients: state.clients.map((client) => {
          if (client.key === (state.clientSelected as IClientState).key) {
            return {
              ...client,
              clientSalePoint: client?.clientSalePoint?.map((point) => {
                if (point.key === (state.pointOfSaleSelected as IPointOfSaleState).key) {
                  return {
                    ...point,
                    point_of_sale_exchange_rates: payload,
                  };
                }
                return point;
              }),
            };
          }
          return client;
        }),
      };
      return updateExchangeRate;
    }

    case ClientsTypes.ADD_NEW_EXCHANGE_RATE: {
      const addNewExchangeRate: IClientsInitialState = {
        ...state,
        pointOfSaleSelected: {
          ...(state.pointOfSaleSelected as IPointOfSaleState),
          point_of_sale_exchange_rates: [
            ...((state.pointOfSaleSelected as IPointOfSaleState)
              .point_of_sale_exchange_rates || []),
            payload.exchangeRate,
          ],
        },
        clientSelected: {
          ...(state.clientSelected as IClientState),
          clientSalePoint: (state.clientSelected as IClientState)?.clientSalePoint?.map(
            (point) => {
              if (point.key === payload.pointOfSaleId) {
                const pointOfSale: IPointOfSaleState = {
                  ...point,
                  point_of_sale_exchange_rates: [
                    ...(point.point_of_sale_exchange_rates || []),
                    payload.exchangeRate,
                  ],
                };
                return pointOfSale;
              }
              return point;
            }
          ),
        },
        clients: state.clients.map((client) => {
          if (client.key === (state.clientSelected as IClientState).key) {
            return {
              ...client,
              clientSalePoint: client?.clientSalePoint?.map((point) => {
                if (point.key === payload.pointOfSaleId) {
                  const pointOfSale: IPointOfSaleState = {
                    ...point,
                    point_of_sale_exchange_rates: [
                      ...(point.point_of_sale_exchange_rates || []),
                      payload.exchangeRate,
                    ],
                  };
                  return pointOfSale;
                }
                return point;
              }),
            };
          }
          return client;
        }),
      };
      return addNewExchangeRate;
    }

    default:
      return state;
  }
}
